import { api_base_url } from "../config/api";

const RETRY_MS = 5000;
const MAX_WAIT_MS = 180000;

/** @typedef {"idle" | "starting" | "ready" | "failed"} WakeStatus */

/** @type {WakeStatus} */
let currentStatus = "idle";
let activeWakes = 0;
/** @type {Set<(status: WakeStatus) => void>} */
const listeners = new Set();

export function getWakeStatus() {
  return currentStatus;
}

export function subscribeWakeStatus(listener) {
  listeners.add(listener);
  listener(currentStatus);
  return () => listeners.delete(listener);
}

function setWakeStatus(status) {
  currentStatus = status;
  listeners.forEach((listener) => {
    try {
      listener(status);
    } catch {
      // UI listeners must not break wake retries
    }
  });
}

function beginWakeUi() {
  activeWakes += 1;
  setWakeStatus("starting");
}

function endWakeUi(status) {
  activeWakes = Math.max(0, activeWakes - 1);
  if (activeWakes === 0 || status === "failed") {
    setWakeStatus(status);
  }
}

function apiOrigin() {
  try {
    return new URL(api_base_url).origin;
  } catch {
    return "https://api.conceptor.ai";
  }
}

export function getWakeUrl() {
  return `${apiOrigin()}/wake`;
}

export function isWakeWorthyStatus(status) {
  return status === 502 || status === 503 || status === 504;
}

export function shouldAttemptWake(error, requestConfig = {}) {
  const status = error?.response?.status;
  const networkFail = !error?.response;
  if (!(networkFail || isWakeWorthyStatus(status))) return false;

  const base = String(requestConfig.baseURL || api_base_url || "");
  const url = String(requestConfig.url || "");
  const origin = apiOrigin();
  return base.includes(origin) || url.includes(origin) || !url.startsWith("http");
}

export async function wakeBackend() {
  try {
    await fetch(getWakeUrl(), { method: "GET", mode: "cors", cache: "no-store" });
  } catch {
    // Instance may still be coming up; retries handle readiness.
  }
}

/**
 * Retry an axios request after waking the backend on cold start.
 * Notifies UI subscribers so users see a "starting servers" message.
 */
export async function retryAfterWake(axiosInstance, originalConfig) {
  beginWakeUi();
  await wakeBackend();

  const started = Date.now();
  let lastError = null;
  let settled = false;

  try {
    while (Date.now() - started < MAX_WAIT_MS) {
      await new Promise((resolve) => setTimeout(resolve, RETRY_MS));
      try {
        const config = { ...originalConfig, __wakeRetried: true };
        const response = await axiosInstance.request(config);
        settled = true;
        endWakeUi("ready");
        return response;
      } catch (err) {
        lastError = err;
        const status = err?.response?.status;
        if (err?.response && !isWakeWorthyStatus(status)) {
          settled = true;
          endWakeUi("idle");
          throw err;
        }
      }
    }

    settled = true;
    endWakeUi("failed");
    throw lastError || new Error("Backend did not become ready in time");
  } catch (err) {
    if (!settled) {
      endWakeUi("failed");
    }
    throw err;
  }
}
